import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { FieldNodeToReference, SignerToReference } from '@modusign/react-signing/types';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';

import { checkIsInOrder } from '@app/features/apply-react-signing/libs/checkIsInOrder';
import { toSigningStateForSetup } from '@app/features/apply-react-signing/libs/signing-mapper/toSigningState';
import { getEmbeddedDraft } from '@app/features/integration/embeddedDraft/lib/getEmbeddedDraft';
import transformDraftDataToDocumentModel from '@app/features/integration/embeddedDraft/lib/transformDraftDataToDocumentModel';
import { usePDF } from '@app/hooks/usePDF';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { isMobileDeviceSize } from '@app/libs/breakpoints';
import { contractSetupDraftFetched } from '@app/store/contract-setup/actions';
import { replaceCarbonCopyList } from '@app/store/contract-setup/reducers/carbonCopies';
import { modalOpen } from '@app/store/modal/actions';
import { getFileFromURL } from '@app/store/transport-layer/files';
import { postDocumentUpload } from '@app/store/transport-layer/upload/document/upload';
import { getUser } from '@app/store/user/selectors';

function hasDifferentSigningDuration(signers: SignerToReference[]): boolean {
  if (signers.length === 0) {
    return false;
  }
  const { signingDuration } = signers[0];
  return signers.some((participant) => participant.signingDuration !== signingDuration);
}

function hasSignatureFieldInEveryDraftSigners(
  signers: SignerToReference[],
  fields: FieldNodeToReference[]
) {
  const signerWithSignatureField = signers.filter((signer) => {
    return fields.some((field) => field.type === 'SIGNATURE' && field.participantId === signer.id);
  });
  return signerWithSignatureField.length === signers.length;
}

type SetupDocumentByDraftError = 'INVALID_SIZE_WITH_NO_SIGNATURE_FIELD';

export const SETUP_DOCUMENT_BY_DRAFT_ERROR_LIST: Array<SetupDocumentByDraftError> = [
  'INVALID_SIZE_WITH_NO_SIGNATURE_FIELD',
];

export function useSetupDocumentByEmbeddedDraft() {
  const dispatch = useDispatch();
  const { user } = useSelector(getUser);
  const { initialize } = useSigningState();

  const { setPDF, resetPDF } = usePDF();

  async function load({ draftId }: { draftId: string }) {
    resetPDF();
    // draft 불러오기
    const draft = await getEmbeddedDraft(draftId);
    // keyword 정보 load위해서
    dispatch(contractSetupDraftFetched({ keywords: draft.keywords || [] }));

    const transformed = transformDraftDataToDocumentModel(draft);

    const signingState = toSigningStateForSetup(
      transformed.attachments,
      transformed.participants,
      transformed.fields,
      transformed.fieldGroups,
      transformed.verifications
    );

    // draft 가공
    if (signingState.participants.filter((p) => p.type === 'SIGNER').length === 0) {
      const id = v4();
      signingState.participants.push({
        id,
        dataLabel: id,
        type: 'SIGNER',
        name: '',
        order: 1,
        index: 0,
        // signingDuration 기본값은 14일 = 14 * 24 * 60 분 = 20160 분 입니다.
        signingDuration: 14 * 24 * 60,
        signingMethod: null,
      });
    }

    const signers = signingState.participants.filter(
      (participant): participant is SignerToReference => participant.type === 'SIGNER'
    );

    if (
      isMobileDeviceSize() &&
      !hasSignatureFieldInEveryDraftSigners(signers, signingState.fields)
    ) {
      throw new Error('INVALID_SIZE_WITH_NO_SIGNATURE_FIELD' as SetupDocumentByDraftError);
    }

    if (hasDifferentSigningDuration(signers)) {
      signers.forEach((signer) => {
        signer.signingDuration = 14 * 24 * 60;
      });

      dispatch(
        modalOpen('CONFIRM', {
          title: '서명 유효기간 안내',
          content: '서명 유효기간을 서명자 별로 지정할 수 없습니다.\n기본값 14일로 적용되었습니다.',
        })
      );
    }

    let currentUserHost = signingState.participants.filter(
      (p) => p.type === 'HOST' && p.contact === user.email
    )[0];
    const shouldAddDefaultHost = !!currentUserHost;

    currentUserHost ??= {
      type: 'HOST',
      id: v4(),
      name: user.name ?? '',
      contact: user.email ?? '',
      dataLabel: v4(),
      locale: 'ko',
    };

    if (shouldAddDefaultHost) {
      signingState.participants.push(currentUserHost);
    }

    const inPersonSignersWithoutHost = signingState.participants.filter(
      (p): p is SignerToReference =>
        p.type === 'SIGNER' && p.signingMethod === 'IN_PERSON' && !p.host
    );

    if (inPersonSignersWithoutHost.length > 0) {
      inPersonSignersWithoutHost.forEach((signer) => {
        signer.host = {
          participantId: currentUserHost.id,
        };
      });
    }

    // (@Juyeong-Byeon) draft로 요청하기에서는 pdf 없이 뷰어에서 파일을 업로드해서 서명 요청을 진행할 수 있다.
    let file: { fileId?: string; url?: string } = {};
    if (draft.file !== null) {
      file = await postDocumentUpload({
        url: draft.file.url,
      });

      if (file.url) {
        const { data: pdf } = await getFileFromURL(file.url);
        setPDF(pdf);
      }
    }

    // (@Juyeong-Byeon) 초기화
    initialize({
      attachments: signingState.attachments,
      fields: signingState.fields,
      verifications: signingState.verifications,
      participants: signingState.participants,
      document: {
        title: draft.title ?? '',
        pdfPassword: draft.pdfPassword,
        pdfFile: {
          id: file.fileId ?? '',
          url: file.url ?? '',
        },
      },
      ui: {
        isInOrder: checkIsInOrder(signingState.participants),
        insertingField: null,
        lastInsertedField: createInitialLastInsertedField(),
      },
    });

    dispatch(
      replaceCarbonCopyList(
        draft.carbonCopies.map(({ contact, locale }) => ({
          addressBookName: null,
          contact,
          id: v4(),
          inputAddBy: 'self',
          locale,
        }))
      )
    );

    // todo: @hyunjunian 임베디드 초안을 템플릿으로 만들었는지는 확인 불가. 추후에 가능하게 해야하면 수정하기
    pushDataLayer({ document: undefined });
    pushDataLayer({
      event: 'documentInitialized',
      document: {
        documentSource: 'embeddedDraft',
        startedAt: Date.now(),
      },
    });
  }

  return {
    load,
  };
}
